import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@iplan/auth/react';

/**
 * Landing route for the OIDC redirect (`/auth/callback?code=...`). The
 * AuthProvider redeems the code on mount; once the session resolves we leave
 * the callback URL for the app. `/auth/signout` reuses this to return home.
 *
 * Lands on `/` rather than a concrete page: the index route owns where the app
 * opens (it redirects to the orders list), so the post-sign-in destination has
 * a single source of truth in `router.tsx`. This previously pointed at `/home`,
 * which was retired along with the card-grid landing page — leaving it would
 * have dropped every sign-in onto the catch-all Not Found route.
 */
export function AuthCallback(): ReactNode {
  const { status } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'authenticated' || status === 'unauthenticated') {
      navigate('/', { replace: true });
    }
  }, [status, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-iplan-surface-muted text-iplan-text-muted">
      Signing you in…
    </div>
  );
}

/** Silent-renew iframe target. Renders nothing; the UserManager processes it. */
export function SilentRenew(): ReactNode {
  return null;
}
